/**
 * Time zones by the city people know, not the IANA id (PC audit 28.09, point 13):
 * the profile showed «Europe/Moscow», and there was no way to change it.
 */

/** Zones people here live in. Any other zone still shows, by its own name. */
export const ZONES: { id: string; city: string }[] = [
  { id: "Europe/Kaliningrad", city: "Калининград" },
  { id: "Europe/Moscow", city: "Москва" },
  { id: "Europe/Samara", city: "Самара" },
  { id: "Asia/Yekaterinburg", city: "Екатеринбург" },
  { id: "Asia/Omsk", city: "Омск" },
  { id: "Asia/Novosibirsk", city: "Новосибирск" },
  { id: "Asia/Krasnoyarsk", city: "Красноярск" },
  { id: "Asia/Irkutsk", city: "Иркутск" },
  { id: "Asia/Yakutsk", city: "Якутск" },
  { id: "Asia/Vladivostok", city: "Владивосток" },
  { id: "Asia/Magadan", city: "Магадан" },
  { id: "Asia/Kamchatka", city: "Петропавловск-Камчатский" },
  { id: "Asia/Almaty", city: "Алматы" },
  { id: "Asia/Qyzylorda", city: "Кызылорда" },
  { id: "Asia/Tashkent", city: "Ташкент" },
  { id: "Asia/Bishkek", city: "Бишкек" },
  { id: "Europe/Minsk", city: "Минск" },
  { id: "Asia/Tbilisi", city: "Тбилиси" },
  { id: "Asia/Yerevan", city: "Ереван" },
  { id: "Asia/Baku", city: "Баку" },
  { id: "Asia/Dubai", city: "Дубай" },
  { id: "Europe/Istanbul", city: "Стамбул" },
  { id: "Europe/Berlin", city: "Берлин" },
  { id: "Europe/London", city: "Лондон" },
  { id: "UTC", city: "UTC" },
];

/** Minutes east of UTC right now (or at `at`); null for a zone this browser does not know. */
export function offsetMinutes(zone: string, at: Date = new Date()): number | null {
  try {
    const name = new Intl.DateTimeFormat("en-US", { timeZone: zone, timeZoneName: "shortOffset" })
      .formatToParts(at).find((p) => p.type === "timeZoneName")?.value ?? "GMT";
    const m = /^GMT(?:([+-])(\d{1,2})(?::(\d{2}))?)?$/.exec(name);
    if (!m) return null;
    if (!m[1]) return 0;
    return (m[1] === "-" ? -1 : 1) * (Number(m[2]) * 60 + Number(m[3] ?? 0));
  } catch {
    return null;
  }
}

/** «UTC+3», «UTC+5:30», «UTC». */
export function offsetLabel(minutes: number): string {
  if (minutes === 0) return "UTC";
  const abs = Math.abs(minutes);
  const tail = abs % 60 ? `:${String(abs % 60).padStart(2, "0")}` : "";
  return `UTC${minutes < 0 ? "−" : "+"}${Math.floor(abs / 60)}${tail}`;
}

/** «Москва (UTC+3)»; an unlisted zone by its last part: «Asia/Ho_Chi_Minh» → «Ho Chi Minh (UTC+7)». */
export function zoneLabel(zone: string, at?: Date): string {
  const city = ZONES.find((z) => z.id === zone)?.city ?? zone.split("/").pop()!.replace(/_/g, " ");
  const minutes = offsetMinutes(zone, at);
  if (zone === "UTC" || minutes === null) return city;
  return `${city} (${offsetLabel(minutes)})`;
}

/** The zone of this device, if the browser tells it. */
export function deviceZone(): string | null {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || null;
  } catch {
    return null;
  }
}

/** Options west to east; the current and the device zone are always among them. */
export function zoneOptions(extra: (string | null | undefined)[] = [], at?: Date): { value: string; label: string }[] {
  const ids = [...ZONES.map((z) => z.id)];
  for (const z of extra) if (z && !ids.includes(z) && offsetMinutes(z, at) !== null) ids.push(z);
  return ids
    .map((id) => ({ id, minutes: offsetMinutes(id, at) ?? 0, label: zoneLabel(id, at) }))
    .sort((a, b) => a.minutes - b.minutes || a.label.localeCompare(b.label, "ru"))
    .map(({ id, label }) => ({ value: id, label }));
}
